/* eslint-disable react/no-multi-comp */
/* eslint-disable react/no-did-update-set-state */
/* eslint-disable no-nested-ternary */

import React from 'react';
import { connect } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { isSafari } from 'react-device-detect';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

import { magazine } from './common/ducks';
import Page from './Page';

class Magazine extends React.Component {
  state = {
    renderMode: 'canvas',
    ratiosByPage: {},
    offscreenReady: false,
  };

  transformRef = React.createRef();

  prevTouchPoint = null;

  _renderedPages = new Set();

  componentDidMount() {
    this._startOffscreenTimer();
  }

  componentWillUnmount() {
    clearTimeout(this._offscreenTimer);
  }

  _startOffscreenTimer() {
    clearTimeout(this._offscreenTimer);
    this._offscreenTimer = setTimeout(() => {
      if (!this.state.offscreenReady) {
        this.setState({ offscreenReady: true });
      }
    }, 3000);
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      pageNumber,
      isSearchMode,
      zoom,
      setOnePager,
      viewportDimensions,
      onePagerMode,
    } = this.props;
    const { ratiosByPage } = this.state;

    // Toggle onePager id dynamic mode selected
    if (
      viewportDimensions && (
      pageNumber !== prevProps.pageNumber ||
      ratiosByPage[pageNumber] !== prevState.ratiosByPage[pageNumber] ||
      viewportDimensions !== prevProps.viewportDimensions)
    ) {
      const { width, height } = viewportDimensions;
      let onePager;
      if (onePagerMode === 0 || onePagerMode === 2) onePager = false;
      else if (onePagerMode === 1) onePager = true;
      else if (onePagerMode === 3)
        onePager = width / height < 1 || ratiosByPage[pageNumber] > 1;
      else onePager = false;
      setOnePager(onePager);
    }

    // Update url if pageNumber changed
    if (pageNumber !== prevProps.pageNumber && pageNumber >= 0) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', pageNumber);
      window.history.replaceState(null, '', url.href);

      // Reset offscreen state so the new spread's pages get a fresh chance
      if (this.state.offscreenReady) {
        this._renderedPages.clear();
        this.setState({ offscreenReady: false });
        this._startOffscreenTimer();
      }
    }

    // refresh required to place search highlights correctly
    if (isSearchMode && prevProps.pageNumber !== pageNumber) {
      // Time used because of animation
      setTimeout(() => this.forceUpdate(), 350);
    }

    if (!zoom && prevProps.pageNumber !== pageNumber && isSafari) {
      this.setState({
        renderMode: 'canvas',
      });
    }
  }

  handleTouchStart = event => {
    if (!event.touches || event.touches.length === 0) return;
    const touch = event.touches[0];
    const { screenX, screenY } = touch;
    this.prevTouchPoint = {
      x: screenX,
      y: screenY,
    };
  };

  handleTouchEnd = () => {
    this.prevTouchPoint = null;
  };

  handleTouchMove = event => {
    const { zoom, previousPage, nextPage } = this.props;

    // Handle swipes
    if (event.touches && event.touches.length === 1 && this.prevTouchPoint) {
      const touch = event.touches[0];
      const { screenX } = touch;
      // not zoomed -> swipe pages
      if (!zoom) {
        if (screenX - this.prevTouchPoint.x < -50) {
          nextPage();
          this.prevTouchPoint = null;
        } else if (screenX - this.prevTouchPoint.x > 50) {
          previousPage();
          this.prevTouchPoint = null;
        }
      }
    }
  };

  handleRatioChange = (pageNumber, ratio) => {
    const { ratiosByPage } = this.state;
    const { onPageRatioUpdate } = this.props;

    if (ratio !== ratiosByPage[pageNumber]) {
      this.setState(state => ({
        ratiosByPage: { ...state.ratiosByPage, [pageNumber]: ratio },
      }));
      if (onPageRatioUpdate) onPageRatioUpdate(pageNumber, ratio);
    }
  }

  // Enable offscreen pages only after visible pages have finished rendering
  handlePageRenderComplete = renderedPageNumber => {
    this._renderedPages.add(renderedPageNumber);

    if (this.state.offscreenReady) return;

    const { onePager, pageNumber, numberOfPages } = this.props;
    const leftPage = onePager ? pageNumber : Math.floor(pageNumber / 2) * 2;
    // Only include valid page numbers (page 0 doesn't exist in PDF)
    const visiblePages = onePager
      ? [leftPage]
      : [leftPage, leftPage + 1];
    const validVisiblePages = visiblePages.filter(p => p >= 1 && p <= (numberOfPages || Infinity));

    const allDone = validVisiblePages.length > 0 && validVisiblePages.every(p => this._renderedPages.has(p));
    if (allDone) {
      clearTimeout(this._offscreenTimer);
      this.setState({ offscreenReady: true });
    }
  }

  render() {
    const {
      pageNumber,
      backwards,
      numberOfPages,
      onePager,
      viewportDimensions,
      isSmallScreen,
      withAnimation,
      zoom,
      zoomScale,
      onClick,
      toggleZoom,
      enhance,
    } = this.props;
    const { ratiosByPage, renderMode } = this.state;

    const leftPage = onePager ? pageNumber : Math.floor(pageNumber / 2) * 2;
    const defaultRatio = 0.707;
    const leftSideRatio = ratiosByPage[leftPage] || defaultRatio;
    const rightSideRatio = onePager ? 0 : ratiosByPage[leftPage + 1] || defaultRatio;

    // Dimensions
    let height = 0;
    if (!viewportDimensions) height = 0;
    else {
      let heightFromWidth = 0;
      height = viewportDimensions.height - (isSmallScreen ? 20 : 100);
      if (onePager) heightFromWidth = viewportDimensions.width / leftSideRatio;
      else if (leftSideRatio === 0) {
        heightFromWidth = viewportDimensions.width / (2 * rightSideRatio);
      } else if (rightSideRatio === 0) {
        heightFromWidth = viewportDimensions.width / (2 * leftSideRatio);
      } else {
        heightFromWidth =
          viewportDimensions.width / (leftSideRatio + rightSideRatio);
      }
      heightFromWidth -= isSmallScreen ? 20 : 100;
      if (heightFromWidth < height) height = heightFromWidth;
    }
    let magazineWidth = (rightSideRatio + leftSideRatio) * height;
    if (!rightSideRatio || !leftSideRatio) magazineWidth *= 2;
    if (onePager) magazineWidth = leftSideRatio * height;

    const commonViewerProps = {
      withAnimation,
      backwards,
      leftPage,
      ratiosByPage,
      height,
      onRatioChange: this.handleRatioChange,
      onRenderComplete: this.handlePageRenderComplete,
      renderMode,
      numberOfPages,
      zoomScale: zoom ? zoomScale || 1 : 1,
      enhance,
      deferOffscreen: !this.state.offscreenReady,
    };

    const doubleClickAnimationDuration = 200;

    return (
      <StyleWrapper>
        <TransformWrapper
          centerOnInit
          limitToBounds
          doubleClick={{
            mode: zoom ? 'reset' : 'zoomIn',
            animationTime: doubleClickAnimationDuration,
          }}
          wrapperClass="react-transform-wrapper"
          ref={this.transformRef}
          panning={{
            disabled: !zoom,
          }}
          onZoom={ref => {
            const scale = ref?.state?.scale ?? ref?.instance?.transformState?.scale;
            if (scale > 1) toggleZoom(true, scale);
          }}
          onZoomStop={ref => {
            const scale = ref?.state?.scale ?? ref?.instance?.transformState?.scale;
            if (scale <= 1) {
              toggleZoom(false, 1);
              if (ref.centerView) ref.centerView(1);
            }
          }}
        >
          <TransformComponent>
            <PerspectiveWrapper
              $onePager={onePager}
              $width={magazineWidth}
              $height={height}
              $leftSideRatio={leftSideRatio}
              $rightSideRatio={rightSideRatio}
              onTouchMove={this.handleTouchMove}
              onTouchStart={this.handleTouchStart}
              onTouchEnd={this.handleTouchEnd}
              onClick={() => {
                onClick();
                // Hacky way of detecting if we zoomed in or out by double clicking
                setTimeout(() => {
                  const ref = this.transformRef.current;
                  const scale = ref?.state?.scale ?? ref?.instance?.transformState?.scale ?? 1;
                  if (!zoom && scale > 1) toggleZoom(true, scale);
                  else if (zoom && scale <= 1) toggleZoom(false, 1);
                }, doubleClickAnimationDuration + 50);
              }}
            >
              <MagazineShadow
                $height={height}
                $leftSideRatio={leftSideRatio}
                $rightSideRatio={rightSideRatio}
                $pageNumber={leftPage}
                $numberOfPages={numberOfPages}
              />
              {!onePager && <TwoPagedViewer {...commonViewerProps} />}
              {onePager && <SinglePageViewer {...commonViewerProps} />}
            </PerspectiveWrapper>
          </TransformComponent>
        </TransformWrapper>
      </StyleWrapper>
    );
  }
}

const StyleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  .react-transform-wrapper {
    overflow: visible;
  }
`;

const PerspectiveWrapper = styled.div`
  user-select: none;
  height: ${props => props.$height}px;
  width: ${props => props.$width}px;

  .page-wrapper {
    left: ${props =>
      props.$onePager
        ? 0
        : !props.$leftSideRatio
        ? props.$rightSideRatio * props.$height
        : props.$leftSideRatio * props.$height}px;
  }
`;

const MagazineShadow = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  }
  &::before {
    width: ${props => props.$leftSideRatio * props.$height}px;
    left: 0;
    transition: opacity ${props => (props.$pageNumber > 0 ? '.3s .3s' : '.2s')};
    opacity: ${props => (props.$pageNumber > 0 ? 1 : 0)};
  }
  &::after {
    width: ${props => props.$rightSideRatio * props.$height}px;
    right: 0;
    transition: opacity
      ${props => (props.$pageNumber < props.$numberOfPages ? '.3s .3s' : '.2s')};
    opacity: ${props => (props.$pageNumber < props.$numberOfPages ? 1 : 0)};
  }
`;

const TwoPagedViewer = ({
  withAnimation,
  backwards,
  leftPage,
  ratiosByPage,
  height,
  onRatioChange,
  onRenderComplete,
  renderMode,
  numberOfPages,
  zoomScale,
  enhance,
  deferOffscreen,
}) => {
  // Transitions
  const unturnedTransition = withAnimation
    ? `right-page${backwards ? '-reverse' : ''}`
    : '';
  const turnedTransition = withAnimation
    ? `left-page${backwards ? '-reverse' : ''}`
    : '';

  const ratio = n => ratiosByPage[n] || 0.707;

  const leftCoverWidth = ratio(leftPage) * height;
  const rightCoverWidth = ratio(leftPage + 1) * height;

  return (
    <>
      {/* Turned Stack */}
      <TransitionGroup component={null}>
        {!deferOffscreen && leftPage - 2 > 0 && (
          <CSSTransition
            key={`page_${leftPage - 2}`}
            classNames={turnedTransition}
            timeout={{ enter: withAnimation ? (backwards ? 10 : 300) : 10, exit: withAnimation ? (backwards ? 300 : 250) : 10 }}
          >
          <Page
            pageNumber={leftPage - 2}
            height={height}
            width={ratio(leftPage - 2) * height}
            maxWidth={leftCoverWidth}
            onRatioChange={onRatioChange}
            renderMode="canvas"
            left
          />
          </CSSTransition>
        )}
        {leftPage > 0 && (
          <CSSTransition
            key={`page_${leftPage}`}
            classNames={turnedTransition}
            timeout={{ enter: withAnimation ? (backwards ? 10 : 300) : 10, exit: withAnimation ? (backwards ? 300 : 250) : 10 }}
          >
          <Page
            scale={zoomScale}
            enhance={enhance}
            pageNumber={leftPage}
            height={height}
            width={leftCoverWidth}
            maxWidth={leftCoverWidth}
            onRatioChange={onRatioChange}
            onRenderComplete={onRenderComplete}
            renderMode={renderMode}
            left
          />
          </CSSTransition>
        )}
        {!deferOffscreen && leftPage < numberOfPages - 1 && (
          <CSSTransition
            key={`page_${leftPage + 2}`}
            classNames={turnedTransition}
            timeout={{ enter: withAnimation ? (backwards ? 10 : 300) : 10, exit: withAnimation ? (backwards ? 300 : 250) : 10 }}
          >
          <Page
            pageNumber={leftPage + 2}
            height={height}
            width={ratio(leftPage + 2) * height}
            maxWidth={leftCoverWidth}
            onRatioChange={onRatioChange}
            renderMode="canvas"
            left
            turned
          />
          </CSSTransition>
        )}
      </TransitionGroup>
      {/* Unturned stack */}
      <TransitionGroup component={null}>
        {!deferOffscreen && leftPage - 1 > 0 && (
          <CSSTransition
            key={`page_${leftPage - 1}`}
            classNames={unturnedTransition}
            timeout={{ enter: withAnimation ? (backwards ? 300 : 10) : 10, exit: withAnimation ? (backwards ? 300 : 250) : 10 }}
          >
          <Page
            pageNumber={leftPage - 1}
            height={height}
            width={ratio(leftPage - 1) * height}
            maxWidth={rightCoverWidth}
            onRatioChange={onRatioChange}
            renderMode="canvas"
            turned
          />
          </CSSTransition>
        )}
        {leftPage < numberOfPages && (
          <CSSTransition
            key={`page_${leftPage + 1}`}
            classNames={unturnedTransition}
            timeout={{ enter: withAnimation ? (backwards ? 300 : 10) : 10, exit: withAnimation ? (backwards ? 300 : 250) : 10 }}
          >
          <Page
            scale={zoomScale}
            enhance={enhance}
            pageNumber={leftPage + 1}
            height={height}
            width={rightCoverWidth}
            maxWidth={rightCoverWidth}
            onRatioChange={onRatioChange}
            onRenderComplete={onRenderComplete}
            renderMode={renderMode}
          />
          </CSSTransition>
        )}
        {!deferOffscreen && leftPage < numberOfPages - 2 && (
          <CSSTransition
            key={`page_${leftPage + 3}`}
            classNames={unturnedTransition}
            timeout={{ enter: withAnimation ? (backwards ? 300 : 10) : 10, exit: withAnimation ? (backwards ? 300 : 250) : 10 }}
          >
          <Page
            pageNumber={leftPage + 3}
            height={height}
            width={ratio(leftPage + 3) * height}
            maxWidth={rightCoverWidth}
            onRatioChange={onRatioChange}
            renderMode="canvas"
          />
          </CSSTransition>
        )}
      </TransitionGroup>
    </>
  );
};

const SinglePageViewer = ({
  withAnimation,
  backwards,
  leftPage,
  ratiosByPage,
  height,
  onRatioChange,
  onRenderComplete,
  renderMode,
  numberOfPages,
  zoomScale,
  enhance,
  deferOffscreen,
}) => {
  const ratio = n => ratiosByPage[n] || 0.707;
  const coverWidth = ratio(leftPage) * height;

  return (
    <TransitionGroup component={null}>
      {!deferOffscreen && leftPage + 1 <= numberOfPages && (
        <CSSTransition
          key={`page_${leftPage + 1}`}
          classNames={`one-page${backwards ? '-reverse' : ''}`}
          timeout={{ enter: withAnimation ? 300 : 10, exit: withAnimation ? 300 : 10 }}
        >
        <Page
          pageNumber={leftPage + 1}
          height={height}
          width={ratio(leftPage + 1) * height}
          maxWidth={coverWidth}
          onRatioChange={onRatioChange}
          renderMode="canvas"
        />
        </CSSTransition>
      )}
      {leftPage <= numberOfPages && (
        <CSSTransition
          key={`page_${leftPage}`}
          classNames={`one-page${backwards ? '-reverse' : ''}`}
          timeout={{ enter: withAnimation ? 300 : 10, exit: withAnimation ? 300 : 10 }}
        >
        <Page
          pageNumber={leftPage}
          height={height}
          width={coverWidth}
          maxWidth={coverWidth}
          onRatioChange={onRatioChange}
          onRenderComplete={onRenderComplete}
          renderMode={renderMode}
          scale={zoomScale}
          enhance={enhance}
        />
        </CSSTransition>
      )}
      {!deferOffscreen && leftPage - 1 > 0 && (
        <CSSTransition
          key={`page_${leftPage - 1}`}
          classNames={`one-page${backwards ? '-reverse' : ''}`}
          timeout={{ enter: withAnimation ? 300 : 10, exit: withAnimation ? 300 : 10 }}
        >
        <Page
          pageNumber={leftPage - 1}
          height={height}
          width={ratio(leftPage - 1) * height}
          maxWidth={coverWidth}
          onRatioChange={onRatioChange}
          renderMode="canvas"
          turned
        />
        </CSSTransition>
      )}
    </TransitionGroup>
  );
};

const mapStateToProps = state => ({
  zoom: state.magazine.zoom,
  zoomScale: state.magazine.zoomScale,
  pageNumber: state.magazine.pageNumber,
  backwards: state.magazine.backwards,
  numberOfPages: state.magazine.numberOfPages,
  onePager: state.magazine.onePager,
  onePagerMode: state.magazine.onePagerMode,
  viewportDimensions: state.magazine.viewportDimensions,
  isSmallScreen: state.magazine.isSmallScreen,
  isSearchMode: state.magazine.isSearchMode,
  withAnimation: state.magazine.withAnimation,
});

const ConnectedMagazine = connect(
  mapStateToProps,
  {
    nextPage: magazine.nextPage,
    previousPage: magazine.previousPage,
    setOnePager: magazine.setOnePager,
    toggleZoom: magazine.toggleZoom,
  },
  null,
  { forwardRef: true }
)(Magazine);

export default React.forwardRef((props, ref) => (
  <ConnectedMagazine {...props} ref={ref} />
));
